/**
 * Sanitizer für Debug-Payloads.
 *
 * Entfernt offensichtlich sensitive Felder, BEVOR ein Payload an einen
 * Admin gerendert wird. Wirkt rekursiv auf Plain-Objects und Arrays. Der
 * Sanitizer ersetzt einen Trefferwert durch das String-Literal
 * `'[redacted]'`, damit erkennbar bleibt, dass etwas vorhanden war.
 *
 * Bewusst NICHT enthalten: hashing/whitelisting. Diese Funktion ist eine
 * Verteidigungslinie, kein vollständiger Privacy-Filter. Aufrufer dürfen
 * sich nicht darauf verlassen, dass alle PII verschwinden — sie sollen
 * `payloadVisibility` korrekt setzen.
 */

const SENSITIVE_KEYS = new Set<string>([
  'token',
  'jwt',
  'authorization',
  'apikey',
  'api_key',
  'secret',
  'password',
  'email',
  'phone',
  'raw_json',
  'source_json',
  'status_json',
  'verification_json',
  // gängige zusätzliche Varianten
  'access_token',
  'refresh_token',
  'id_token',
  'cookie',
  'set-cookie',
  'bearer',
]);

const MAX_DEPTH = 6;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function sanitizeDebugPayload(input: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) return '[truncated]';
  if (input == null) return input;
  if (typeof input === 'string') {
    // Bearer-Tokens im Klartext rausstreichen
    if (/bearer\s+[a-z0-9._-]{16,}/i.test(input)) return '[redacted]';
    return input;
  }
  if (typeof input !== 'object') return input;
  if (Array.isArray(input)) {
    return input.map((v) => sanitizeDebugPayload(v, depth + 1));
  }
  if (!isPlainObject(input)) return '[object]';
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (SENSITIVE_KEYS.has(k.toLowerCase())) {
      out[k] = '[redacted]';
      continue;
    }
    out[k] = sanitizeDebugPayload(v, depth + 1);
  }
  return out;
}

/**
 * Erzeugt eine kurze, redaktierte Zusammenfassung (Top-Level-Keys + Typen),
 * damit auch bei `payloadVisibility='redacted'` ein nützlicher Hinweis
 * sichtbar bleibt, ohne Werte zu leaken.
 */
export function summarizeDebugPayload(input: unknown): string {
  if (input == null) return 'null';
  if (Array.isArray(input)) return `array(${input.length})`;
  if (isPlainObject(input)) {
    const keys = Object.keys(input).slice(0, 8);
    return `{ ${keys.join(', ')}${Object.keys(input).length > keys.length ? ', …' : ''} }`;
  }
  return typeof input;
}
